import React from "react";
import VASGraph from "../../../../components/feedback/VASGraph";
import ExpertFeedbackBand from "./ExpertFeedbackBand";
import FeedbackMetricSummary from "../reference-feedback/FeedbackMetricSummary";
import ReferenceFeedbackPresentation from "../reference-feedback/ReferenceFeedbackPresentation";
import { toVoiceAssessmentVASGraphProps } from "../reference-feedback/voiceAssessmentGraphPresentation";
import {
  deriveExpertBandPresentation,
  formatExpertBandValue,
  resolvePunchListAgreementExplanation,
  resolveExpertBandNarrative,
  resolvePunchListFeedbackCaption,
  resolveSharedAgreementCommentary,
  resolveSharedLowEndAgreement,
} from "./expertBandPresentation";
import { renderExactTokenizedCopy } from "../../../program-presentation/programTemporalTokenPresentation";
import {
  projectRepeatedJudgmentDirections,
  repeatedJudgmentDirectionMessage,
  repeatedJudgmentSummaryMessages,
  selectGovernedAgreementWindow,
} from "./repeatedJudgmentDirection";

export { selectGovernedAgreementWindow } from "./repeatedJudgmentDirection";

function EstablishedReferenceGraphic({ parameter, presentation, firstLearnerValue, difference }) {
  const { raw } = presentation;
  const first = firstLearnerValue !== null && firstLearnerValue !== undefined && Number.isFinite(Number(firstLearnerValue))
    ? Number(firstLearnerValue)
    : null;
  const graphProps = toVoiceAssessmentVASGraphProps({
    attributeName: parameter,
    learnerValue: raw.learnerValue,
    referenceValues: raw.observations,
  });
  return <div className="av-expert-band__established-reference">
    {first !== null && <p className="av-expert-band__mirror-difference" data-testid="expert-band-mirror-difference">
      Your two ratings are {formatExpertBandValue(Math.abs(difference ?? raw.learnerValue - first))} points apart.
    </p>}
    <VASGraph
      {...graphProps}
      sessionValues={first === null ? [] : [{ value: first }]}
      sessionLegendLabel="Your earlier rating"
      ariaLabel={`${parameter} learner and PVQD reference ratings`}
    />
    <FeedbackMetricSummary items={[
      { label: "Your rating", value: formatExpertBandValue(raw.learnerValue) },
      { label: "Central value", value: formatExpertBandValue(raw.mean) },
      { label: "Spread (SD)", value: formatExpertBandValue(raw.sd) },
    ]} />
  </div>;
}

export default function ExpertBandFeedback({
  rows = [],
  capability,
  variant,
  outerBoundsByParameter = {},
  priorLearnerValues = {},
  differences = {},
  mirrorRows = [],
  mirrorCopy = null,
  firstPassDate = null,
  targetReplay = null,
  showStandaloneTargetReplayInstruction = true,
  presentationContract = null,
  presentationVariant = "ordinary",
  headingHelp = null,
}) {
  const usePunchListBand = presentationContract?.contractVersion === "byce-g3-punchlist-19-feedback-band.v1";
  const available = rows.map(row => {
    const agreementWindow = variant?.outerBandMode === "agreement-window"
      ? selectGovernedAgreementWindow(row.reference)
      : null;
    if (variant?.outerBandMode === "agreement-window" && !agreementWindow) return null;
    const presentation = deriveExpertBandPresentation({
      reference: row.reference,
      learnerValue: row.learnerValue,
      outerBounds: variant?.outerBandMode === "externally-governed"
        ? outerBoundsByParameter[row.sourceParameterLabel]
        : agreementWindow || undefined,
    });
    return {
      row,
      presentation,
      agreement: usePunchListBand ? resolvePunchListAgreementExplanation({
        presentation,
        parameter: row.sourceParameterLabel || row.constructLabel,
        copy: presentationContract.copy,
      }) : null,
    };
  }).filter(item => item?.presentation);
  if (!available.length) return null;
  const directionProjection = projectRepeatedJudgmentDirections({
    mirrorRows,
    referenceRows: rows,
  });
  const sharedLowEndAgreement = usePunchListBand ? resolveSharedLowEndAgreement({
    explanations: available.map(({ row, agreement }) => ({
      parameter: row.sourceParameterLabel || row.constructLabel,
      explanation: agreement,
    })),
    copy: presentationContract.copy,
  }) : null;
  const sharedAgreementCommentary = sharedLowEndAgreement || (usePunchListBand
    ? resolveSharedAgreementCommentary({
        explanations: available.map(({ row, agreement }) => ({
          parameter: row.sourceParameterLabel || row.constructLabel,
          explanation: agreement,
        })),
      })
    : null);

  const introduction = sharedAgreementCommentary ? <p
      className="av-expert-band-feedback__voice-agreement"
      data-copy-block={sharedAgreementCommentary.blockId}
    >{sharedAgreementCommentary.text}</p> : null;
  const items = available.map(({ row, presentation }) => {
      const narrative = usePunchListBand ? null : resolveExpertBandNarrative({ presentation, narrative: capability.narrative });
      const mirror = mirrorRows.find(candidate => candidate.parameterId === row.parameterId || candidate.parameter === row.sourceParameterLabel);
      const prior = mirror?.firstValue ?? priorLearnerValues[row.sourceParameterLabel];
      const mirrorText = mirror && mirrorCopy?.message
        ? renderExactTokenizedCopy(mirrorCopy.message, {
            date: firstPassDate,
            parameter: mirror.parameter || row.sourceParameterLabel,
            first: formatExpertBandValue(mirror.firstValue),
            second: formatExpertBandValue(mirror.secondValue),
          })
        : null;
      const caption = usePunchListBand ? resolvePunchListFeedbackCaption({
        presentation,
        presentationVariant,
        parameter: row.sourceParameterLabel || row.constructLabel,
        copy: presentationContract.copy,
        includeAgreement: !sharedAgreementCommentary,
      }) : [];
      const parameter = row.sourceParameterLabel || row.constructLabel;
      const direction = directionProjection?.rows.find(candidate =>
        candidate.parameterId === mirror?.parameterId || candidate.parameter === parameter
      );
      const directionText = repeatedJudgmentDirectionMessage(direction);
      return {
        key: row.sourceParameterLabel,
        parameter,
        content: <>
        {mirrorText && <p data-copy-block="ST-30">{mirrorText}</p>}
        {usePunchListBand && mirror && <p className="av-expert-band__mirror-difference" data-testid="expert-band-mirror-difference">
          Your two ratings are {formatExpertBandValue(mirror.absoluteDifference ?? differences[row.sourceParameterLabel])} points apart.
        </p>}
        {usePunchListBand && directionText && <p data-testid="probe-direction-feedback">{directionText}</p>}
        {usePunchListBand ? <ExpertFeedbackBand
          parameter={row.sourceParameterLabel || row.constructLabel}
          presentation={presentation}
          learnerConfidence={row.learnerConfidence}
          coreVisible={presentationContract.variants?.[presentationVariant]?.coreVisible !== false}
          priorLearnerValue={variant?.learnerMarks === "prior-and-current" ? prior : undefined}
          panelRaterMeans={variant?.panelRaterMarks ? row.panelRaterMeans : undefined}
          panelRaterRequiredCount={variant?.panelRaterMarks?.requiredCount}
        /> : <EstablishedReferenceGraphic
          parameter={row.sourceParameterLabel || row.constructLabel}
          presentation={presentation}
          firstLearnerValue={variant?.learnerMarks === "prior-and-current" ? prior : undefined}
          difference={mirror?.absoluteDifference ?? differences[row.sourceParameterLabel]}
        />}
        {caption.map(sentence => <p key={sentence.blockId} data-copy-block={sentence.blockId}>{sentence.text}</p>)}
        {narrative && <p data-copy-block={narrative.blockId}>{narrative.text}</p>}
        </>,
      };
    });
  const footer = <>
    {directionProjection
      ? <div data-testid="probe-direction-summary">
          {repeatedJudgmentSummaryMessages(directionProjection).map(message => <p key={message}>{message}</p>)}
        </div>
      : mirrorRows.length > 0 && mirrorCopy?.framingMessage && <p data-copy-block="ST-31">{mirrorCopy.framingMessage}</p>}
    {showStandaloneTargetReplayInstruction && capability.targetReplay?.message && <p data-copy-block={capability.targetReplay.blockId}>{capability.targetReplay.message}</p>}
  </>;

  return <ReferenceFeedbackPresentation
    headingHelp={headingHelp}
    introduction={introduction}
    items={items}
    target={targetReplay}
    footer={footer}
  />;
}

export { EstablishedReferenceGraphic };
